import { mapBasketModifiers } from '@/entities/basket/model/mapBasketItem'
import { mapAccrualList } from '@/entities/finance/model/mapFinance'
import { mapGeoPoint } from '@/entities/order/lib/mapGeoPoint'
import { normalizeOrderStatus } from '@/entities/order/model/normalizeOrderStatus'
import { mapOrderPaymentFields } from '@/entities/payment/model/orderPayment'
import { resolveProductImageUrl } from '@/entities/product/lib/resolveProductImages'

export function mapOrder(dto) {
  if (!dto) {
    return null
  }

  const payload = dto?.data ?? dto
  const restaurant = resolveRestaurant(payload)
  const client = mapOrderClient(payload)

  return {
    id: String(payload.id ?? payload._id ?? ''),
    status: normalizeOrderStatus(payload.status),
    restaurantId: restaurant.id,
    restaurantTitle: restaurant.title,
    restaurantAddress: restaurant.address,
    restaurantLocation: restaurant.location,
    deliveryAddress: payload.address ?? '',
    deliveryLocation: mapGeoPoint(payload.location),
    items: mapOrderItems(payload.items),
    itemsCount: resolveItemsCount(payload),
    client,
    clientName: client.displayName,
    totalAmount: Number(payload.total_price ?? 0),
    deliveryFee: Number(payload.delivery_fee ?? 0),
    ...mapOrderPaymentFields(payload),
    deliveryAt: payload.delivery_at ?? payload.delivered_at ?? null,
    pickupReadyAt: payload.pickup_ready_at ?? null,
    courier: mapCourier(payload.courier),
    createdAt: payload.created_at ?? null,
    updatedAt: payload.updated_at ?? null,
    timestamps: payload.timestamps ?? {},
    settlementRestoPaid: Boolean(payload.settlement_resto_paid),
    settlementRestoAmountVnd: Number(payload.settlement_resto_amount_vnd ?? 0),
    accruals: mapAccrualList(payload.accruals),
  }
}

export function mapOrderList(dtos) {
  if (!Array.isArray(dtos?.items)) {
    return { items: [], total: Number(dtos?.total) || 0 }
  }
  return {
    items: dtos.items.map(mapOrder).filter(Boolean),
    total: Number(dtos.total) || 0,
  }
}

function mapOrderItems(items) {
  if (!Array.isArray(items)) {
    return []
  }

  return items.map((item) => ({
    id: String(item.product_id ?? item.id ?? ''),
    title: item.title ?? '',
    description: item.description ?? '',
    quantity: Number(item.quantity ?? 1),
    price: Number(item.price ?? 0),
    weightGrams:
      item.weight_g != null && Number.isFinite(Number(item.weight_g))
        ? Number(item.weight_g)
        : null,
    imageUrl: resolveProductImageUrl(item),
    modifiers: mapBasketModifiers(item.modifiers),
  }))
}

function resolveItemsCount(payload) {
  if (payload.items_count != null && Number.isFinite(Number(payload.items_count))) {
    return Math.max(0, Math.floor(Number(payload.items_count)))
  }
  if (!Array.isArray(payload.items)) {
    return 0
  }
  return payload.items.reduce((sum, item) => sum + Math.max(1, Math.floor(Number(item.quantity) || 1)), 0)
}

/** Список заказов: client + items_count. В GET /order/:id client обычно нет. */
function mapOrderClient(payload) {
  const raw = payload?.client
  if (!raw || typeof raw !== 'object') {
    return {
      id: null,
      firstName: null,
      lastName: null,
      displayName: String(payload?.client_name ?? payload?.customer_name ?? '').trim(),
    }
  }

  const firstName = String(raw.first_name ?? '').trim()
  const lastName = String(raw.last_name ?? '').trim()
  const displayName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    String(raw.name ?? raw.email ?? raw.phone_number ?? '').trim() ||
    String(payload?.client_name ?? payload?.customer_name ?? '').trim()

  return {
    id: raw.id != null && raw.id !== '' ? String(raw.id) : null,
    firstName: firstName || null,
    lastName: lastName || null,
    displayName,
  }
}

function resolveRestaurant(payload) {
  const restoId =
    payload.resto_id != null && payload.resto_id !== '' ? String(payload.resto_id) : null
  const nested = payload.restaurant

  if (nested && typeof nested === 'object') {
    return {
      id:
        nested.id != null && nested.id !== ''
          ? String(nested.id)
          : restoId,
      title: String(nested.title ?? '').trim(),
      address: nested.address ? String(nested.address).trim() : '',
      location: mapGeoPoint(nested.location),
    }
  }

  return {
    id: restoId,
    title: '',
    address: '',
    location: null,
  }
}

function mapCourierPath(rawPath) {
  if (!Array.isArray(rawPath)) {
    return []
  }

  return rawPath
    .map((point) => {
      const location = mapGeoPoint(point)
      if (!location) {
        return null
      }

      return {
        ...location,
        recordedAt: point.recorded_at ?? null,
      }
    })
    .filter(Boolean)
}

function mapCourier(raw) {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  const id = raw.id != null && raw.id !== '' ? String(raw.id) : null
  const firstName = String(raw.first_name ?? '').trim()
  const lastName = String(raw.last_name ?? '').trim()
  const legacyName = String(raw.name ?? raw.display_name ?? '').trim()
  const name = [firstName, lastName].filter(Boolean).join(' ').trim() || legacyName
  const location = mapGeoPoint(raw.location)
  const path = mapCourierPath(raw.path)

  if (!id && !name && !location && path.length === 0) {
    return null
  }

  return {
    id,
    name: name || null,
    photoUrl: raw.photo_url ?? raw.photoUrl ?? null,
    location,
    path,
  }
}
